import {
  DELETE_PRODUCT_BY_ID_ENDPOINT,
  FETCH_PAYMENT_PROVIDERS_ENDPOINT,
  GET_PRODUCT_BY_SKU_ENDPOINT,
  LEGACY_PRODUCT_BY_ID,
  PRODUCT_BY_ID_ENDPOINT,
  PRODUCT_BY_SLUG,
  PRODUCT_BY_SLUG_EXTENDED,
  PRODUCT_BY_SLUG_LISTING,
  PRODUCT_BY_SLUG_LISTING_SIMILAR,
  PRODUCT_MORE_ITEMS,
  REPOP_PRODUCT_ENDPOINT,
  UPDATE_SAVED_PRODUCT_ENDPOINT,
  USER_PRODUCT_BY_SLUG,
} from '@/constants/endpoints.ts';
import http, { typedHttp } from '@/modules/http';
import { replacePathParams } from '@/modules/http/replacePathParams';
import { type AxiosCompatibleResponse } from '@/modules/http/types';
import {
  type MoreItemsQuery,
  type ProductBySlugResponse,
} from '@/modules/product/aliases.ts';
import {
  type LegacyProductById,
  type UserProductBySlug,
} from '@/modules/product/types.ts';

export function getProductById({
  productId,
  sessionId,
  include_offers = false,
  include_seller_info = false,
}: {
  productId: string;
  sessionId?: string | null;
  include_offers?: boolean;
  include_seller_info?: boolean;
}) {
  return typedHttp.get(
    replacePathParams(PRODUCT_BY_ID_ENDPOINT, { productId }),
    {
      headers: {
        ...(sessionId && { 'Depop-Session-Id': sessionId }),
      },
      params: {
        include_offers,
        include_seller_info,
      },
      withDeviceId: true,
      withAuth: true,
    }
  );
}

export function deleteProductById({ productId }: { productId: number }) {
  return typedHttp.delete(
    replacePathParams(DELETE_PRODUCT_BY_ID_ENDPOINT, {
      productId: productId.toString(),
    }),
    {
      withAuth: true,
    }
  );
}

export function getLegacyProductById({
  productId,
  sessionId,
}: {
  productId: string;
  sessionId?: string | null;
}): Promise<AxiosCompatibleResponse<LegacyProductById>> {
  return http.get(LEGACY_PRODUCT_BY_ID.replace(':productId', productId), {
    headers: {
      ...(sessionId && { 'Depop-Session-Id': sessionId }),
    },
  });
}

export function getProductMoreItems(productId: string, params: MoreItemsQuery) {
  return typedHttp.get(
    replacePathParams(PRODUCT_MORE_ITEMS, { id: productId }),
    {
      params,
      withDeviceId: true,
      withAuth: true,
    }
  );
}

export function getProductBySlug({
  slug,
}: {
  slug: string;
}): Promise<AxiosCompatibleResponse<ProductBySlugResponse>> {
  return typedHttp.get(replacePathParams(PRODUCT_BY_SLUG, { slug }), {
    withAuth: true,
    withDeviceId: true,
  });
}

export function getProductBySlugExtended({
  lang,
  slug,
  headers,
}: {
  slug: string;
  lang?: string;
  headers?: Record<string, string>;
}) {
  return typedHttp.get(replacePathParams(PRODUCT_BY_SLUG_EXTENDED, { slug }), {
    withAuth: true,
    withDeviceId: true,
    params: {
      lang,
      include_sold_sizes: true,
    },
    headers,
  });
}

export function getUserProductBySlug({
  lang,
  slug,
  includeSellerShippingInfo,
}: {
  slug: string;
  includeSellerShippingInfo?: boolean;
  lang?: string;
}): Promise<AxiosCompatibleResponse<UserProductBySlug>> {
  return http.get(USER_PRODUCT_BY_SLUG.replace(':slug', slug), {
    withAuth: true,
    params: {
      lang,
      includeSellerShippingInfo,
      camel_case: true,
    },
  });
}

export function getProductListingBySlug({ slug }: { slug: string }) {
  return typedHttp.get(replacePathParams(PRODUCT_BY_SLUG_LISTING, { slug }), {
    withAuth: true,
  });
}

export function getProductListingBySlugForSimilar({ slug }: { slug: string }) {
  return typedHttp.get(
    replacePathParams(PRODUCT_BY_SLUG_LISTING_SIMILAR, { slug }),
    {
      withAuth: true,
    }
  );
}

export function getRepopProduct(purchaseId: string, purchaseItemId: string) {
  return typedHttp.get(
    replacePathParams(REPOP_PRODUCT_ENDPOINT, { purchaseId, purchaseItemId }),
    {
      withAuth: true,
    }
  );
}

export function deleteProductSave(productId: number) {
  return typedHttp.delete(
    replacePathParams(UPDATE_SAVED_PRODUCT_ENDPOINT, {
      productId: productId.toString(),
    }),
    {
      withAuth: true,
    }
  );
}

export function addProductSave(productId: number) {
  return typedHttp.put(
    replacePathParams(UPDATE_SAVED_PRODUCT_ENDPOINT, {
      productId: productId.toString(),
    }),
    null,
    { withAuth: true }
  );
}

export function getProductPaymentProviders({
  sellerId,
  currency,
  withAuth = false,
}: {
  sellerId: number;
  currency: string;
  withAuth?: boolean;
}) {
  return typedHttp.get(
    replacePathParams(FETCH_PAYMENT_PROVIDERS_ENDPOINT, {
      sellerId: sellerId.toString(),
    }),
    {
      withAuth,
      params: { currency },
    }
  );
}

export function getProductBySKU({ sku }: { sku: string }) {
  return typedHttp.get(
    replacePathParams(GET_PRODUCT_BY_SKU_ENDPOINT, {
      sku: encodeURIComponent(sku),
    }),
    {
      withAuth: true,
    }
  );
}
